import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import Button from '../common/Button.js'
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay.js';
import { formatIstDateTime } from '../../utils/localDate.js';


function InfoField({ label, value, className = '', children }) {
  if (!children && (value === null || value === undefined || value === '')) return null;
  return (
    <div className={className}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      {children || (
        <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
          {value}
        </p>
      )}
    </div>
  );
}

function SectionCard({ title, children, className = 'mb-4 sm:mb-6' }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 sm:p-5 ${className}`}>
      <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        {title}
      </h2>
      {children}
    </div>
  );
}

const ViewMissing = () => {
  const { t } = useTranslation();
  const { missingId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [missingData, setMissingData] = useState(null)

  useEffect(() => {
    fetchMissing()
  }, [missingId])

  const fetchMissing = async () => {
    try {
      setLoading(true)
      const response = await shipmentMissingService.getMissing(missingId)
      const missing = response.data.missing || response.data.data

      if (missing) {
        setMissingData(missing)
      } else {
        Alert.error("Missing shipment record not found")
      }
    } catch (error) {
      console.error("Error fetching missing:", error)
      Alert.error(error.response?.data?.error || "Failed to load missing shipment")
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading missing shipment..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!missingData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[40vh] sm:min-h-[60vh]">
          <div className="text-center px-4">
            <p className="text-gray-600 font-semibold text-base sm:text-lg">Missing record not found</p>
            <Button variant="secondary" className="mt-4" onClick={() => navigate("/missing-shipments")}>
              Back to Missing Shipments
            </Button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
              <AlertTriangle className="w-6 h-6 text-buttonhoverTxt shrink-0" />
              Missing Shipment Details
            </h1>
          </div>
          <div className="flex flex-col gap-2 w-full lg:w-auto md:flex-row md:flex-wrap md:gap-3">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold uppercase border ${getStatusBadgeColor(missingData.approval_status)}`}>
              {missingData.approval_status}
            </span>
            <Button variant="primary" onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}>
              View Shipment
            </Button>
            <Button
              variant="secondary"
              icon={ArrowLeft}
              mobileIconOnly
              mobileLabel="Back to Missing Shipments"
              onClick={() => navigate("/missing-shipments")}
            >
              Back to Missing Shipments
            </Button>
          </div>
        </div>

        <SectionCard title="Missing Information">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-6">
            <InfoField label="Missing ID" value={`#${missingData.missing_id}`} />
            <InfoField label="Shipment ID">
              <button
                type="button"
                onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}
                className="font-medium text-blue-600 hover:text-blue-700 hover:underline text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 text-left w-full"
              >
                #{missingData.shipment_id}
              </button>
            </InfoField>
            <InfoField label={t('common.createdBy')} value={formatAuditUser(missingData, 'created_by')} />
            <InfoField label="Quantity Missing" value={missingData.quantity_missing || "N/A"} />
            <InfoField
              label="Created Date"
              value={missingData.created_at ? formatIstDateTime(missingData.created_at) : "N/A"}
            />
            <InfoField label="Status">
              <span className={`inline-block px-3 py-1 mt-1 rounded-full text-xs font-semibold uppercase border ${getStatusBadgeColor(missingData.approval_status)}`}>
                {missingData.approval_status}
              </span>
            </InfoField>
          </div>
        </SectionCard>

        <SectionCard title="Reason for Missing">
          <p className="text-gray-900 whitespace-pre-wrap leading-relaxed bg-gray-100 p-3 rounded-xl">
            {missingData.reason}
          </p>
        </SectionCard>

        {missingData.missing_details && (
          <SectionCard title="Additional Details">
            <p className="text-gray-900 whitespace-pre-wrap leading-relaxed bg-gray-100 p-3 rounded-xl">
              {missingData.missing_details}
            </p>
          </SectionCard>
        )}

        {missingData.rejection_reason && (
          <SectionCard title="Rejection Reason">
            <p className="text-red-800 whitespace-pre-wrap leading-relaxed bg-red-50 p-3 rounded-xl border border-red-100">
              {missingData.rejection_reason}
            </p>
          </SectionCard>
        )}
      </div>
    </DashboardLayout>
  )
}

export default ViewMissing;
